/**
 * Member Activity → Credit history (DNKADM-0430 P1-D): this member's credit-movement
 * ledger, server paged, on the shared list toolbar with its filters and page in the URL.
 *
 * It is the member-scoped `CreditsLedger` (DNKADM-0154 U2) moved beside the other member
 * tabs, the same way `MemberTransactionsTab` wraps the payments table (DNKADM-0150): the
 * same `listCreditTransactions` read pre-filtered by `contactId`, the same
 * `CreditTransactionsTable` in `memberScoped` mode (no Contact/Actor columns), the same
 * filters and defaults. What changed is the toolbar (the shared `ListToolbar`, with Clear)
 * and that every filter and the page live in the URL (`chFrom`, `chTo`, `chReason`,
 * `chBucket`, `chPage`, see `memberDetailView.ts`).
 *
 * Phase 1 has no sort here: the list is server paged and dink-api takes no sort param yet
 * (DNKAPI-0615). The API parser's newest-first re-sort is left in place.
 */
import { useCallback, useMemo } from 'react';
import { Box, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CREDIT_TRANSACTION_REASONS, listCreditTransactions } from '@/api/creditTransactions';
import { useCreditBucketPickList } from '@/api/creditBuckets';
import { IconAction } from '@/components/IconAction';
import { ListSelectFilter, ListToolbar } from '@/components/list/ListToolbar';
import {
  parseOption,
  parsePage,
  toPageParam,
  useListUrlState,
} from '@/components/list/useListUrlState';
import { CreditTransactionsTable } from '@/features/transaction-ledger/CreditTransactionsTable';
import { humanizeToken } from '@/features/transaction-ledger/transactionFormat';
import { useResource } from '@/hooks/useResource';
import { useServiceLocation } from '@/location/useServiceLocation';
import { useCalendarZone } from '@/location/useCalendarZone';
import { Icon, inkAlpha, resolveTokens } from '@/theme';
import { creditHistoryUrlDefaults, parseCreditHistoryDate } from './memberDetailView';
import { ScreenError } from '@/components/ScreenError';

const PAGE_SIZE = 20;
const SKELETON_ROWS = 6;

const REASON_OPTIONS = CREDIT_TRANSACTION_REASONS.map((r) => ({
  value: r,
  label: humanizeToken(r),
}));

export function MemberCreditHistory({ contactId }: { contactId: string }) {
  const { locations, selectedId } = useServiceLocation();
  // The last-30-days default, on the location calendar (DNKADM-0439). Computed once per zone:
  // the hook depends on the object, which changes at most once, when the locations load.
  // The same zone renders the rows: location, else tenant, else device (DNKADM-0478).
  const calendarZone = useCalendarZone();
  const venueTimeZone = calendarZone;
  const defaults = useMemo(() => creditHistoryUrlDefaults(calendarZone), [calendarZone]);
  const { values, set } = useListUrlState(defaults);
  // Validated so a hand-edited or stale link falls back instead of breaking. `''` is
  // "no filter" (and, for a date, "no bound").
  const from = parseCreditHistoryDate(values.chFrom, defaults.chFrom);
  const to = parseCreditHistoryDate(values.chTo, defaults.chTo);
  const reason = parseOption<string>(values.chReason, CREDIT_TRANSACTION_REASONS, '');
  // A bucket id is opaque and its pick list loads later, so it is passed through as is;
  // the server scopes it to the tenant.
  const bucketId = values.chBucket;
  const page = parsePage(values.chPage);

  // Bucket pick-list for the filter (best-effort — a failure leaves just "All buckets").
  const buckets = useCreditBucketPickList();
  const bucketOptions = (buckets.items ?? []).map((b) => ({ value: b.id, label: b.name }));
  const resolveLocationName = useCallback(
    (id: string) => locations.find((l) => l.id === id)?.name ?? null,
    [locations],
  );

  const txns = useResource(
    useCallback(
      () =>
        listCreditTransactions({
          page,
          size: PAGE_SIZE,
          contactId,
          ...(reason === '' ? {} : { reason }),
          ...(bucketId === '' ? {} : { creditBucketId: bucketId }),
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
        }),
      [page, contactId, reason, bucketId, from, to],
    ),
    [page, contactId, reason, bucketId, from, to],
  );

  const items = txns.data?.items ?? [];
  const totalPages = txns.data?.totalPages ?? null;

  // One `set` per action; every filter change resets the page.
  const filtersActive =
    from !== defaults.chFrom || to !== defaults.chTo || reason !== '' || bucketId !== '';
  // Clear returns to the default view (last 30 days, every reason and bucket).
  const clearFilters = () =>
    set({ chFrom: null, chTo: null, chReason: null, chBucket: null, chPage: null });

  return (
    <Box>
      <ListToolbar
        dateRange={{
          from,
          to,
          onFromChange: (v) => set({ chFrom: v, chPage: null }),
          onToChange: (v) => set({ chTo: v, chPage: null }),
        }}
        filters={
          <>
            <ListSelectFilter
              label="Reason"
              value={reason}
              onChange={(v) => set({ chReason: v, chPage: null })}
              allLabel="All reasons"
              options={REASON_OPTIONS}
            />
            <ListSelectFilter
              label="Bucket"
              value={bucketId}
              onChange={(v) => set({ chBucket: v, chPage: null })}
              allLabel="All buckets"
              options={bucketOptions}
            />
          </>
        }
        onClear={clearFilters}
        canClear={filtersActive}
      />

      {/* Credit movement isn't location-scoped — the credit-transactions API has no
          location param, so this ledger stays tenant-wide even when a location is
          selected in the app bar (DNKADM-0153). DNKADM-0160 uses that selected
          location only for display/link safety, not query scoping. */}
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Credit movement isn’t scoped by location — this shows every location.
      </Typography>

      {txns.status === 'error' ? (
        <ScreenError
          variant="panel"
          error={txns.error}
          what="credits"
          onRetry={txns.refetch}
          retryMessage="Something went wrong while loading the ledger."
          sx={{ mt: 2.25 }}
        />
      ) : (
        <Paper elevation={1} sx={{ mt: 2.25, overflow: 'hidden' }}>
          {txns.status === 'loading' && <SkeletonBlock />}
          {txns.status === 'ready' && items.length === 0 && <EmptyState />}
          {txns.status === 'ready' && items.length > 0 && (
            <CreditTransactionsTable
              rows={items}
              memberScoped
              currentLocationId={selectedId}
              resolveLocationName={resolveLocationName}
              timeZone={venueTimeZone}
            />
          )}
          {/* Past the first page the pager stays even when the page is empty (a stale
              link), so Previous is the way back. */}
          {txns.status === 'ready' && (items.length > 0 || page > 1) && (
            <Pagination
              page={page}
              totalPages={totalPages}
              itemCount={items.length}
              onPrev={() => set({ chPage: toPageParam(Math.max(1, page - 1)) })}
              onNext={() => set({ chPage: toPageParam(page + 1) })}
            />
          )}
        </Paper>
      )}
    </Box>
  );
}

function SkeletonBlock() {
  return (
    <Box sx={{ px: 3, py: 2 }}>
      {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
        <Skeleton key={`sk-${i}`} height={28} sx={{ my: 0.5 }} />
      ))}
    </Box>
  );
}

function EmptyState() {
  const theme = useTheme();
  return (
    <Box sx={{ py: 7, textAlign: 'center' }}>
      <Icon name="account_balance" sx={{ fontSize: 46, color: inkAlpha(theme, 0.2) }} />
      <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>
        No transactions for these filters
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 380, mx: 'auto' }}>
        Try widening the date range or clearing a filter.
      </Typography>
    </Box>
  );
}

function Pagination({
  page,
  totalPages,
  itemCount,
  onPrev,
  onNext,
}: {
  page: number;
  totalPages: number | null;
  itemCount: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  // With a known page count, stop at the last page; otherwise offer Next until a
  // short (final) page arrives.
  const t = resolveTokens(useTheme());
  const atLastPage = totalPages !== null ? page >= Math.max(1, totalPages) : itemCount < PAGE_SIZE;

  return (
    <Stack
      component="nav"
      aria-label="Pagination"
      direction="row"
      sx={{
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
        px: 2,
        py: 1.5,
        borderTop: `1px solid ${t.lineFaint}`,
      }}
    >
      <Typography variant="body2" aria-current="page">
        Page {page}
      </Typography>
      <IconAction label="Previous page" size="small" onClick={onPrev} disabled={page <= 1}>
        <Icon name="chevron_left" />
      </IconAction>
      <IconAction label="Next page" size="small" onClick={onNext} disabled={atLastPage}>
        <Icon name="chevron_right" />
      </IconAction>
    </Stack>
  );
}
